import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Users, CreditCard, UserCheck, UserX, TrendingUp, Calendar, Shield, Trash2, Gift, X, Search, Globe, Bell, DollarSign, Download, Database, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { adminAPI } from '../services/api';
// import { toast } from '../hooks/use-toast'; // Temporarily disabled to avoid portal errors

// Simple toast replacement
const toast = ({ title, description, variant }) => {
  console.log(`[${variant === 'destructive' ? 'ERROR' : 'INFO'}] ${title}: ${description}`);
  // Show a simple alert for important messages
  if (variant === 'destructive') {
    alert(`Error: ${description}`);
  }
};

const AdminPanel = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeviceLimitDialog, setShowDeviceLimitDialog] = useState(false);
  const [showMakeFreeDialog, setShowMakeFreeDialog] = useState(false);
  const [deviceLimit, setDeviceLimit] = useState(2);
  const [userDevices, setUserDevices] = useState([]);
  const [showDevicesDialog, setShowDevicesDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpiredTrialsDialog, setShowExpiredTrialsDialog] = useState(false);
  const [expiredTrialUsers, setExpiredTrialUsers] = useState([]);
  const [selectedExpiredUsers, setSelectedExpiredUsers] = useState([]);
  const [massRemindersEnabled, setMassRemindersEnabled] = useState(() => {
    return localStorage.getItem('massRemindersEnabled') !== 'false';
  });
  const [apptStatsPeriod, setApptStatsPeriod] = useState('day');
  const [apptStatsDate, setApptStatsDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [apptStats, setApptStats] = useState(null);
  const [apptStatsLoading, setApptStatsLoading] = useState(false);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers()
      ]);
      setStats(statsRes.data);
      
      // Sort users: admins first, then by creation date
      const sortedUsers = usersRes.data.sort((a, b) => {
        // Admins always come first
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        
        // If both are admins or both are users, sort by creation date (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadApptStats = async (period, date) => {
    setApptStatsLoading(true);
    try {
      const res = await adminAPI.getAppointmentStats(period, date);
      setApptStats(res.data);
    } catch (error) {
      console.error('Error loading appointment stats:', error);
    } finally {
      setApptStatsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadApptStats(apptStatsPeriod, apptStatsDate);
    }
  }, [apptStatsPeriod, apptStatsDate]);

  const navigateDate = (direction) => {
    const d = new Date(apptStatsDate);
    if (apptStatsPeriod === 'day') d.setDate(d.getDate() + direction);
    else if (apptStatsPeriod === 'month') d.setMonth(d.getMonth() + direction);
    else d.setFullYear(d.getFullYear() + direction);
    setApptStatsDate(d.toISOString().split('T')[0]);
  };

  const getDateLabel = () => {
    const d = new Date(apptStatsDate + 'T12:00:00');
    if (apptStatsPeriod === 'day') return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (apptStatsPeriod === 'month') return d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    return d.getFullYear().toString();
  };


  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  const handleMakeFree = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser._id}/make-free?device_limit=${deviceLimit}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to make user free');
      
      toast({
        title: 'Usuario actualizado',
        description: `Usuario convertido a plan gratuito con ${deviceLimit} dispositivo(s)`,
      });
      setShowMakeFreeDialog(false);
      setSelectedUser(null);
      await loadData();
    } catch (error) {
      console.error('Error making user free:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el usuario',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFree = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/remove-free`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to remove free plan');
      
      toast({
        title: 'Plan gratuito removido',
        description: 'El usuario ha sido revertido a trial expirado',
      });
      await loadData();
    } catch (error) {
      console.error('Error removing free plan:', error);
      toast({
        title: 'Error',
        description: 'No se pudo remover el plan gratuito',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateDeviceLimit = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser._id}/device-limit?device_limit=${deviceLimit}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to update device limit');
      
      toast({
        title: 'Límite actualizado',
        description: `Límite de dispositivos establecido a ${deviceLimit}`,
      });
      setShowDeviceLimitDialog(false);
      setSelectedUser(null);
      await loadData();
    } catch (error) {
      console.error('Error updating device limit:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el límite de dispositivos',
        variant: 'destructive',
      });
    }
  };

  const handleViewDevices = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/devices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch devices');
      
      const data = await response.json();
      setUserDevices(data.devices || []);
      setShowDevicesDialog(true);
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los dispositivos',
        variant: 'destructive',
      });
    }
  };

  const handleLoadExpiredTrials = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/expired-trials`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch expired trials');
      
      const data = await response.json();
      setExpiredTrialUsers(data);
      setSelectedExpiredUsers([]);
      setShowExpiredTrialsDialog(true);
    } catch (error) {
      console.error('Error fetching expired trials:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los usuarios con trial expirado',
        variant: 'destructive',
      });
    }
  };

  const handleBulkDeleteExpired = async () => {
    if (selectedExpiredUsers.length === 0) {
      toast({
        title: 'Atención',
        description: 'Selecciona al menos un usuario para eliminar',
        variant: 'destructive',
      });
      return;
    }

    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar ${selectedExpiredUsers.length} usuario(s)? Esta acción no se puede deshacer.`
    );

    if (!confirmDelete) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedExpiredUsers)
      });
      
      if (!response.ok) throw new Error('Failed to delete users');
      
      const result = await response.json();
      
      toast({
        title: 'Usuarios eliminados',
        description: `Se eliminaron ${result.deleted_count} usuario(s) exitosamente`,
      });
      
      setShowExpiredTrialsDialog(false);
      setExpiredTrialUsers([]);
      setSelectedExpiredUsers([]);
      await loadData();
    } catch (error) {
      console.error('Error deleting users:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron eliminar los usuarios',
        variant: 'destructive',
      });
    }
  };

  const toggleExpiredUserSelection = (userId) => {
    setSelectedExpiredUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const toggleSelectAllExpired = () => {
    if (selectedExpiredUsers.length === expiredTrialUsers.length) {
      setSelectedExpiredUsers([]);
    } else {
      setSelectedExpiredUsers(expiredTrialUsers.map(u => u._id));
    }
  };

  const handleToggleExportPermission = async (userId, currentPermission) => {
    const newPermission = !currentPermission;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/toggle-export?can_export=${newPermission}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to toggle export permission');
      
      toast({
        title: 'Permiso actualizado',
        description: `Exportación de datos ${newPermission ? 'habilitada' : 'deshabilitada'}`,
      });
      
      await loadData();
    } catch (error) {
      console.error('Error toggling export permission:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el permiso',
        variant: 'destructive',
      });
    }
  };

  const handleToggleBanner = async (userId, currentSetting) => {
    const newSetting = !currentSetting;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/toggle-banner?show_banner=${newSetting}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to toggle banner setting');
      
      toast({
        title: 'Configuración actualizada',
        description: `Banner de upgrade ${newSetting ? 'habilitado' : 'deshabilitado'}`,
      });
      
      await loadData();
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la configuración',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await adminAPI.deleteUser(selectedUser._id);
      toast({
        title: 'Usuario eliminado',
        description: 'El usuario y todos sus datos han sido eliminados',
      });
      setShowDeleteDialog(false);
      setSelectedUser(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo eliminar el usuario',
        variant: 'destructive',
      });
    }
  };

  const getSubscriptionBadge = (subscription) => {
    if (!subscription) return <Badge variant="secondary">Sin suscripción</Badge>;
    
    const statusColors = {
      active: 'bg-green-500 text-white',
      trial: 'bg-blue-500 text-white',
      expired: 'bg-red-500 text-white',
      cancelled: 'bg-gray-500 text-white'
    };

    return (
      <Badge className={statusColors[subscription.status] || 'bg-gray-500 text-white'}>
        {subscription.status === 'trial' ? 'Prueba' : 
         subscription.status === 'active' ? 'Activa' :
         subscription.status === 'expired' ? 'Expirada' : 'Cancelada'}
        {subscription.plan === 'free' && ' - FREE'}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const handleToggleMassReminders = (enabled) => {
    setMassRemindersEnabled(enabled);
    localStorage.setItem('massRemindersEnabled', enabled.toString());
    toast({
      title: enabled ? 'Función activada' : 'Función desactivada',
      description: enabled 
        ? 'Los recordatorios masivos están ahora disponibles' 
        : 'Los recordatorios masivos están ahora ocultos',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al Dashboard
              </Button>
              <div className="flex items-center space-x-2">
                <Shield className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate('/admin/landing')}
                variant="outline"
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                <Globe className="w-4 h-4 mr-2" />
                Landing Page
              </Button>
              <Button
                onClick={() => navigate('/admin/push')}
                variant="outline"
                className="border-green-300 text-green-600 hover:bg-green-50"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notificaciones Push
              </Button>
              <Button
                onClick={() => navigate('/admin/finance')}
                variant="outline"
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Finanzas
              </Button>
              <Button
                data-testid="backup-btn"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('notimax_token') || localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/export`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                      const errorText = await response.text();
                      alert(`Error: ${response.status} - ${errorText}`);
                      return;
                    }
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `notimax_backup_${new Date().toISOString().split('T')[0]}.json`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();
                    alert('Backup descargado correctamente');
                  } catch (error) {
                    console.error('Error:', error);
                    alert(`Error de conexión: ${error.message}`);
                  }
                }}
                variant="outline"
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Backup
              </Button>
              <span className="text-sm text-gray-600">{currentUser?.name}</span>
              <Badge className="bg-purple-600 text-white">Admin</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Usuarios</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-gray-900">{stats?.total_users || 0}</div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Suscripciones Activas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-green-600">{stats?.active_subscriptions || 0}</div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">En Prueba</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-yellow-600">{stats?.trial_subscriptions || 0}</div>
                <TrendingUp className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Citas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-purple-600">{stats?.total_appointments || 0}</div>
                <Calendar className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Appointment Stats by User */}
        <Card className="mb-8 border-l-4 border-l-indigo-500" data-testid="appointment-stats-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-500" />
                  Citas por Usuario
                </CardTitle>
                <CardDescription className="mt-1">
                  Consulta la cantidad de citas de cada usuario
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {['day', 'month', 'year'].map((p) => (
                  <button
                    key={p}
                    data-testid={`period-btn-${p}`}
                    onClick={() => setApptStatsPeriod(p)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      apptStatsPeriod === p
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {p === 'day' ? 'Día' : p === 'month' ? 'Mes' : 'Año'}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Date Navigation */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <button
                data-testid="stats-date-prev"
                onClick={() => navigateDate(-1)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-gray-800 capitalize" data-testid="stats-date-label">
                  {getDateLabel()}
                </span>
                <input
                  type="date"
                  value={apptStatsDate}
                  onChange={(e) => setApptStatsDate(e.target.value)}
                  className="w-8 h-8 opacity-0 absolute"
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <button
                data-testid="stats-date-next"
                onClick={() => navigateDate(1)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => setApptStatsDate(new Date().toISOString().split('T')[0])}
                className="ml-2 px-3 py-1 text-xs font-medium rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
              >
                Hoy
              </button>
            </div>

            {apptStatsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : apptStats ? (
              <>
                {/* Total summary */}
                <div className="bg-indigo-50 rounded-lg p-4 mb-4 text-center">
                  <p className="text-sm text-indigo-600 font-medium">Total de citas en este período</p>
                  <p className="text-3xl font-bold text-indigo-700" data-testid="stats-total-count">
                    {apptStats.totalAppointments}
                  </p>
                </div>

                {apptStats.users.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">No hay citas en este período</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Usuario</TableHead>
                          <TableHead className="text-center">Total</TableHead>
                          <TableHead className="text-center">Confirmadas</TableHead>
                          <TableHead className="text-center">Pendientes</TableHead>
                          <TableHead className="text-center">Canceladas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {apptStats.users.map((u) => (
                          <TableRow key={u.userId} data-testid={`stats-row-${u.userId}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-gray-900">{u.userName}</p>
                                <p className="text-xs text-gray-500">{u.userEmail}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-bold text-lg">
                                {u.total}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
                                {u.confirmed}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-semibold">
                                {u.pending}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-sm font-semibold">
                                {u.cancelled}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
        <Card className="mb-8 border-l-4 border-l-red-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <UserX className="w-5 h-5 text-red-500" />
                  Usuarios con Trial Expirado
                </CardTitle>
                <CardDescription className="mt-2">
                  Depura usuarios cuyo período de prueba terminó y no se convirtieron en premium
                </CardDescription>
              </div>
              <Button
                onClick={handleLoadExpiredTrials}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Ver y Depurar
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Usuarios Gratuitos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{stats?.free_subscriptions || 0}</span>
                <Gift className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Suscripciones Stripe</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{stats?.stripe_subscriptions || 0}</span>
                <CreditCard className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Suscripciones Expiradas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-red-600">{stats?.expired_subscriptions || 0}</span>
                <UserX className="w-6 h-6 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feature Configuration Section */}
        <Card className="mb-8 border-l-4 border-l-orange-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-500" />
              Configuración de Funciones
            </CardTitle>
            <CardDescription>
              Activa o desactiva funciones específicas de la aplicación
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Recordatorios Masivos por WhatsApp</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Permite enviar recordatorios masivos a múltiples pacientes desde el calendario
                  </p>
                </div>
                <Switch
                  checked={massRemindersEnabled}
                  onCheckedChange={handleToggleMassReminders}
                  className="ml-4"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Gestión de Usuarios</CardTitle>
            <CardDescription>
              Lista completa de usuarios registrados en la plataforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Mostrando {filteredUsers.length} de {users.length} usuarios
              </p>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Suscripción</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Puede Exportar</TableHead>
                    <TableHead>Ver Banner</TableHead>
                    <TableHead>Fecha Registro</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        {searchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda' : 'No hay usuarios registrados'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user._id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getSubscriptionBadge(user.subscription)}
                      </TableCell>
                      <TableCell>
                        {user.subscription?.plan || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleExportPermission(user._id, user.can_export !== false)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            user.can_export !== false 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                          title="Click para activar/desactivar exportación"
                        >
                          {user.can_export !== false ? '✓ Permitido' : '✗ Bloqueado'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleBanner(user._id, user.show_upgrade_banner !== false)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            user.show_upgrade_banner !== false 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title="Click para mostrar/ocultar banner de upgrade"
                        >
                          {user.show_upgrade_banner !== false ? '👁️ Visible' : '🚫 Oculto'}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-1 flex-wrap gap-1">
                          {user.subscription?.plan !== 'free' && (
                            <Button
                              onClick={() => {
                                setSelectedUser(user);
                                setDeviceLimit(2);
                                setShowMakeFreeDialog(true);
                              }}
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              title="Convertir a usuario gratuito"
                            >
                              <Gift className="w-3 h-3" />
                            </Button>
                          )}
                          {user.subscription?.plan === 'free' && (
                            <Button
                              onClick={() => handleRemoveFree(user._id)}
                              variant="outline"
                              size="sm"
                              className="text-orange-600 hover:text-orange-700"
                              title="Quitar plan gratuito"
                            >
                              <UserX className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              setSelectedUser(user);
                              setDeviceLimit(user.device_limit || 2);
                              setShowDeviceLimitDialog(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            title="Configurar límite de dispositivos"
                          >
                            📱
                          </Button>
                          <Button
                            onClick={() => handleViewDevices(user._id)}
                            variant="outline"
                            size="sm"
                            className="text-purple-600 hover:text-purple-700"
                            title="Ver dispositivos activos"
                          >
                            👁️
                          </Button>
                          {user._id !== (currentUser._id || currentUser.id) && (
                            <Button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowDeleteDialog(true);
                              }}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              title="Eliminar usuario"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Make Free Dialog */}
      {showMakeFreeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Convertir a Usuario Gratuito</h2>
                <button
                  onClick={() => setShowMakeFreeDialog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Convertir a <strong>{selectedUser?.name}</strong> a usuario gratuito permanente.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de dispositivos permitidos
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={deviceLimit}
                  onChange={(e) => setDeviceLimit(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Entre 1 y 10 dispositivos</p>
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowMakeFreeDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleMakeFree} className="bg-green-600 hover:bg-green-700">
                  <Gift className="w-4 h-4 mr-2" />
                  Hacer Gratuito
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Limit Dialog */}
      {showDeviceLimitDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Configurar Límite de Dispositivos</h2>
                <button
                  onClick={() => setShowDeviceLimitDialog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Configurar límite de dispositivos para <strong>{selectedUser?.name}</strong>
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número máximo de dispositivos
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={deviceLimit}
                  onChange={(e) => setDeviceLimit(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Límite actual: {selectedUser?.device_limit || 'Por defecto según plan'}
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeviceLimitDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdateDeviceLimit} className="bg-blue-600 hover:bg-blue-700">
                  Actualizar Límite
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devices Dialog */}
      {showDevicesDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Dispositivos Activos</h2>
                <button
                  onClick={() => {
                    setShowDevicesDialog(false);
                    setUserDevices([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {userDevices.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No hay dispositivos activos</p>
              ) : (
                <div className="space-y-3">
                  {userDevices.map((device) => (
                    <div key={device._id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">{device.deviceName}</h3>
                          <p className="text-sm text-gray-600">{device.browser}</p>
                          <p className="text-sm text-gray-600">{device.os}</p>
                          {device.ipAddress && (
                            <p className="text-xs text-gray-500 mt-1">IP: {device.ipAddress}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            Último acceso: {new Date(device.lastActive).toLocaleString('es-ES')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-6">
                <Button onClick={() => {
                  setShowDevicesDialog(false);
                  setUserDevices([]);
                }}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expired Trials Dialog */}
      {showExpiredTrialsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Usuarios con Trial Expirado</h2>
                <button
                  onClick={() => setShowExpiredTrialsDialog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {expiredTrialUsers.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No hay usuarios con trial expirado</p>
              ) : (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      {expiredTrialUsers.length} usuario(s) con trial expirado encontrado(s)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={toggleSelectAllExpired}
                        variant="outline"
                        size="sm"
                      >
                        {selectedExpiredUsers.length === expiredTrialUsers.length ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                      </Button>
                      <Button
                        onClick={handleBulkDeleteExpired}
                        variant="destructive"
                        size="sm"
                        disabled={selectedExpiredUsers.length === 0}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar Seleccionados ({selectedExpiredUsers.length})
                      </Button>
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <input
                              type="checkbox"
                              checked={selectedExpiredUsers.length === expiredTrialUsers.length}
                              onChange={toggleSelectAllExpired}
                              className="rounded"
                            />
                          </TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Fecha Registro</TableHead>
                          <TableHead>Fecha Expiración</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expiredTrialUsers.map((user) => (
                          <TableRow key={user._id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedExpiredUsers.includes(user._id)}
                                onChange={() => toggleExpiredUserSelection(user._id)}
                                className="rounded"
                              />
                            </TableCell>
                            <TableCell className="font-medium">{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatDate(user.created_at)}
                            </TableCell>
                            <TableCell className="text-sm text-red-600">
                              {formatDate(user.subscription?.trial_end)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              <div className="flex justify-end mt-6">
                <Button onClick={() => setShowExpiredTrialsDialog(false)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">¿Eliminar usuario?</h2>
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Esta acción eliminará permanentemente al usuario <strong>{selectedUser?.name}</strong> y todos sus datos asociados (citas, etiquetas, recomendaciones). Esta acción no se puede deshacer.
              </p>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  Cancelar
                </Button>
                <Button variant="destructive" onClick={handleDeleteUser}>
                  Eliminar Usuario
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
